import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Eye, 
  MousePointer, 
  Heart, 
  Star, 
  TrendingUp, 
  Globe, 
  Phone, 
  MapPin,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw
} from 'lucide-react';

const API = `${window.location.origin}/api`;

function StatCard({ icon: Icon, label, value, trend, color = 'blue' }) {
  const colorClasses = {
    blue: 'from-blue-500/20 to-cyan-500/20 border-blue-500/30 text-blue-400',
    green: 'from-green-500/20 to-emerald-500/20 border-green-500/30 text-green-400',
    purple: 'from-purple-500/20 to-pink-500/20 border-purple-500/30 text-purple-400',
    yellow: 'from-yellow-500/20 to-orange-500/20 border-yellow-500/30 text-yellow-400',
    red: 'from-red-500/20 to-rose-500/20 border-red-500/30 text-red-400'
  };

  return (
    <div className={`p-4 rounded-xl bg-gradient-to-br ${colorClasses[color]} border`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5" />
        {trend !== undefined && (
          <span className={`flex items-center text-xs ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-zinc-500 text-sm">{label}</p>
    </div>
  );
}

function SimpleBarChart({ data, label }) {
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d.views), 1);
  
  return (
    <div className="space-y-2">
      <p className="text-zinc-500 text-sm mb-4">{label}</p>
      <div className="flex items-end gap-1 h-32">
        {data.slice(-14).map((d, i) => (
          <div
            key={i}
            className="flex-1 bg-blue-500/20 hover:bg-blue-500/40 rounded-t transition-colors"
            style={{ height: `${(d.views / maxValue) * 100}%`, minHeight: d.views > 0 ? '4px' : '0' }}
            title={`${d.date}: ${d.views} views`}
          />
        ))}
      </div>
      <div className="flex justify-between text-xs text-zinc-600">
        <span>{data.slice(-14)[0]?.date?.slice(5) || ''}</span>
        <span>{data.slice(-1)[0]?.date?.slice(5) || ''}</span>
      </div>
    </div>
  );
}

export default function AnalyticsDashboard({ businessId, businessName }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/analytics/business/${businessId}?days=${period}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
        setError(null);
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to load analytics');
      }
    } catch (err) {
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId) {
      fetchAnalytics();
    }
  }, [businessId, period]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 skeleton rounded" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <BarChart3 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
        <p className="text-zinc-500">{error}</p>
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">{businessName || 'Analytics'}</h2>
          <p className="text-zinc-500 text-sm">Last {period} days performance</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/20"
            data-testid="analytics-period-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={fetchAnalytics}
            className="w-9 h-9 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
            data-testid="refresh-analytics"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Eye}
          label="Page Views"
          value={analytics.total_page_views}
          color="blue"
        />
        <StatCard
          icon={Globe}
          label="Website Clicks"
          value={analytics.website_clicks}
          color="green"
        />
        <StatCard
          icon={Heart}
          label="Favorites"
          value={analytics.favorites_count}
          color="red"
        />
        <StatCard
          icon={Star}
          label="Avg. Rating"
          value={analytics.average_rating?.toFixed(1) || '0.0'}
          color="yellow"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center gap-2 text-zinc-400 mb-2">
            <Phone className="w-4 h-4" />
            <span className="text-sm">Phone Clicks</span>
          </div>
          <p className="text-xl font-bold text-white">{analytics.phone_clicks}</p>
        </div>
        
        <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center gap-2 text-zinc-400 mb-2">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">Directions</span>
          </div>
          <p className="text-xl font-bold text-white">{analytics.directions_clicks}</p>
        </div>
        
        <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center gap-2 text-zinc-400 mb-2">
            <MousePointer className="w-4 h-4" />
            <span className="text-sm">Social Clicks</span>
          </div>
          <p className="text-xl font-bold text-white">{analytics.social_clicks}</p>
        </div>
        
        <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center gap-2 text-zinc-400 mb-2">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">Search Impressions</span>
          </div>
          <p className="text-xl font-bold text-white">{analytics.search_impressions}</p>
        </div>
      </div>

      {/* Views Chart */}
      {analytics.daily_views && analytics.daily_views.length > 0 && (
        <div className="p-6 rounded-xl bg-zinc-900/50 border border-zinc-800">
          <SimpleBarChart data={analytics.daily_views} label="Daily Page Views" />
        </div>
      )}

      {/* Reviews Summary */}
      <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Star className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="font-medium text-white">Reviews</p>
              <p className="text-zinc-500 text-sm">{analytics.review_count} total reviews</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-white">{analytics.average_rating?.toFixed(1) || '0.0'}</p>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-3 h-3 ${
                    star <= Math.round(analytics.average_rating || 0)
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-zinc-600'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
